
import 'intl-tel-input/build/css/intlTelInput.css'

import intlTelInput from 'intl-tel-input'
import { computed, defineComponent, nextTick, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useStore } from 'vuex'

import CustomerLifetimeStats from '@/components/customers/CustomerLifetimeStats.vue'
import CustomerProductsOrdered from '@/components/customers/CustomerProductsOrdered.vue'
import CustomerRevenueCard from '@/components/customers/CustomerRevenueCard.vue'
import DeliveryAddressesSection from '@/components/customers/DeliveryAddressesSection.vue'
import ProfileAttrs from '@/components/customers/profile/ProfileAttrs.vue'
import ProfileNotes from '@/components/customers/profile/ProfileNotes.vue'
import ProfileTags from '@/components/customers/profile/ProfileTags.vue'
import RecordButton from '@/components/customers/record/RecordButton.vue'
import RecordCard from '@/components/customers/record/RecordCard.vue'
import RecordRow from '@/components/customers/record/RecordRow.vue'
import ResellerCustomerBanner from '@/components/customers/ResellerCustomerBanner.vue'
import { contactSinceLabel, fetchCustomerPair, useCustomerRecord } from '@/composables/useCustomerRecord'
import { useToast } from '@/composables/useToast'
import { MEDIUM_DATE } from '@/helpers/dates'
import { isValidEmail } from '@/helpers/email'
import privilegeService from '@/services/privilege.service'
import api from '@/store/api'
import { mapRelationship } from '@/store/mappers'

const initialData = () => ({
  phoneRaw: '',
  phoneFormatted: '',
  email: '',
  customerId: null,
  leadChannels: [],
  deliveryAddresses: []
})

const initialLocal = () => ({
  status: {
    customer: {
      isLoading: false
    },
    addresses: {
      isLoading: false,
      isEmpty: false,
      hasError: false
    }
  },
  code: null,
  editingCode: false,
  editingPhone: false,
  editingDisplayName: false,
  displayNameDraft: '',
  editingEmail: false,
  emailDraft: '',
  phoneNumber: '',
  iti: null
})

export default defineComponent({
  components: {
    ResellerCustomerBanner,
    DeliveryAddressesSection,
    RecordButton,
    RecordCard,
    RecordRow,
    ProfileAttrs,
    ProfileNotes,
    ProfileTags,
    CustomerLifetimeStats,
    CustomerProductsOrdered,
    CustomerRevenueCard
  },
  setup() {
    const store = useStore()
    const phonebox = ref(null)
    const { showToast } = useToast()
    const { lifetimeState, lifetime } = useCustomerRecord()

    const name = computed(
      () => mapRelationship({ type: 'conversation_user_pair', id: route.params.id })?.attributes.display_name
    )

    const canUpdateCustomerProfile = computed(() => {
      return privilegeService.canAccessResourceAction('customer_profile', null, 'update')
    })

    const saveName = name => {
      api.call('PUT', `conversation_user_pairs/${route.params.id}`, {
        conversation_user_pair: {
          display_name: name
        }
      })
    }

    const startEditingDisplayName = () => {
      local.value.displayNameDraft = name.value || ''
      local.value.editingDisplayName = true
    }

    const saveDisplayName = () => {
      saveName(local.value.displayNameDraft)
      local.value.editingDisplayName = false
    }

    const saveCode = () => {
      api
        .call('PUT', `conversation_user_pairs/${route.params.id}?included=referral_discount_codes`, {
          conversation_user_pair: {
            referral_discount_codes_attributes: [{ id: data.value.code.id, code: local.value.code }]
          }
        })
        .then(response => {
          data.value.code = mapRelationship(response.data.data.relationships.referral_discount_codes.data?.[0])
        })
      local.value.editingCode = false
    }

    const copyCode = () => {
      const code = data.value.code?.attributes.code
      if (!code || !navigator.clipboard) return
      navigator.clipboard
        .writeText(code)
        .then(() => showToast('Referral code copied'))
        .catch(() => showToast("Couldn't copy the code.", { type: 'error' }))
    }

    const updateCustomer = customer =>
      api.call('PATCH', `conversation_user_pairs/${route.params.id}/customer/${data.value.customerId}`, { customer })

    const savePhone = () => {
      if (local.value.iti && local.value.iti.isValidNumber() && data.value.customerId) {
        updateCustomer({ phone: local.value.iti.getNumber() }).then(() => {
          getCustomers()
        })
        local.value.editingPhone = false
        local.value.iti?.destroy()
        local.value.iti = null
      }
    }

    const startEditingEmail = () => {
      local.value.emailDraft = data.value.email || ''
      local.value.editingEmail = true
    }

    const saveEmail = () => {
      const email = local.value.emailDraft.trim()
      if (email && !isValidEmail(email)) {
        showToast('Enter a valid email address', { type: 'error' })
        return
      }
      updateCustomer({ email })
        .then(() => {
          local.value.editingEmail = false
          getCustomers()
        })
        .catch(error => {
          showToast(error.response?.data?.errors?.join(', ') || "Couldn't update the email. Please try again.", {
            type: 'error'
          })
        })
    }

    const route = useRoute()

    const data = ref(initialData())

    const local = ref(initialLocal())

    watch(
      () => local.value.code,
      () => {
        if (!local.value.code) return
        local.value.code = local.value.code
          .toUpperCase()
          .replace(/\s/g, '-')
          .replace(/[^A-Za-z0-9_-]*$/g, '')
      }
    )

    watch(
      () => local.value.editingPhone,
      async () => {
        if (local.value.editingPhone) {
          await nextTick()
          if (phonebox.value) {
            local.value.iti = intlTelInput(phonebox.value.querySelector('input'), {
              utilsScript: 'https://cdn.jsdelivr.net/npm/intl-tel-input@19.5.6/build/js/utils.js',
              preferredCountries: [{ USA: 'US', AUS: 'AU', CAN: 'CA', GBR: 'GB' }[store.getters.country]],
              countrySearch: false,
              initialCountry: 'auto',
              geoIpLookup: function (callback) {
                callback(store.getters.country)
              }
            })
            if (data.value.phoneRaw) {
              local.value.iti.setNumber(data.value.phoneRaw)
            }
          }
        } else {
          local.value.iti?.destroy()
          local.value.iti = null
        }
      }
    )

    const getCustomers = () => {
      const id = route.params.id
      local.value.status.customer.isLoading = true
      api.call('GET', `conversation_user_pairs/${id}/customer`).then(response => {
        if (id !== route.params.id) return
        const attrs = response.data.data.attributes

        data.value.phoneRaw = attrs?.phone_number?.raw ?? null
        data.value.phoneFormatted = attrs?.phone_number?.formatted ?? null
        data.value.email = attrs?.email ?? null
        data.value.profileLink = attrs?.profile_link ?? null
        data.value.leadChannels = attrs?.lead_channels ?? []
        data.value.customerId = response.data.data.id

        local.value.status.customer.isLoading = false
      })
    }

    const getDeliveryAddresses = () => {
      const id = route.params.id
      local.value.status.addresses.isLoading = true
      api
        .call('GET', `conversation_user_pairs/${id}/customer/delivery_address`)
        .then(response => {
          if (id !== route.params.id) return
          data.value.deliveryAddresses = response.data.data || []
          local.value.status.addresses.isLoading = false
          local.value.status.addresses.isEmpty = data.value.deliveryAddresses.length === 0
        })
        .catch(() => {
          if (id !== route.params.id) return
          local.value.status.addresses.isLoading = false
          local.value.status.addresses.hasError = true
        })
    }

    const getConversationUserPairs = () => {
      const id = route.params.id
      fetchCustomerPair(id).then(response => {
        if (id !== route.params.id) return
        data.value.code = mapRelationship(response.data.data.relationships.referral_discount_codes.data?.[0])
        local.value.code = data.value.code?.attributes.code
      })
    }

    const conversationUserPair = computed(() =>
      mapRelationship({ type: 'conversation_user_pair', id: route.params.id })
    )

    const contactSince = computed(() => contactSinceLabel(conversationUserPair.value))

    const leadChannelMarkers = computed(() =>
      data.value.leadChannels.map(leadChannel => ({
        channel: leadChannel.channel,
        text: leadChannel.label,
        tooltip: `First on ${MEDIUM_DATE.format(new Date(leadChannel.first_at))}`
      }))
    )

    const loadCustomer = () => {
      getConversationUserPairs()
      getCustomers()
      getDeliveryAddresses()
    }

    watch(
      () => route.params.id,
      () => {
        local.value.iti?.destroy()
        local.value = initialLocal()
        data.value = initialData()
        loadCustomer()
      }
    )

    onMounted(loadCustomer)

    return {
      data,
      local,
      phonebox,
      route,
      name,
      saveName,
      startEditingDisplayName,
      saveDisplayName,
      saveCode,
      copyCode,
      savePhone,
      startEditingEmail,
      saveEmail,
      conversationUserPair,
      contactSince,
      leadChannelMarkers,
      getDeliveryAddresses,
      canUpdateCustomerProfile,
      lifetimeState,
      lifetime
    }
  }
})
